import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import { authAPI, settingsAPI } from '../services/api';
import { PUBLIC_ACCOUNT_SIGNUP_ENABLED } from '../utils/runtimeConfig';
import toast from 'react-hot-toast';

const BICICLO_LOGO_SRC = '/branding/biciclo-logo.png?v=20260605-2';
const INITIAL_TENANT_FORM = {
  account_code: '',
  name: '',
  db_url: '',
  initial_admin: {
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    password2: '',
  },
  locality_count: 1,
  initial_locations: [{
    store_name: '',
    code: '',
    city: '',
    address_line: '',
    postal_code: '',
    phone: '',
  }],
};

function getApiErrorMessage(error, fallback) {
  const payload = error?.response?.data;

  if (payload?.error) {
    return payload.error;
  }

  if (payload?.detail) {
    return payload.detail;
  }

  if (payload && typeof payload === 'object') {
    const firstEntry = Object.values(payload).find(Boolean);
    if (Array.isArray(firstEntry) && firstEntry.length > 0) {
      return firstEntry.join(', ');
    }
    if (typeof firstEntry === 'string') {
      return firstEntry;
    }
  }

  return fallback;
}

const createEmptyTenantLocation = () => ({
  store_name: '',
  code: '',
  city: '',
  address_line: '',
  postal_code: '',
  phone: '',
});

export default function Login({ initialTenantFormOpen = false }) {
  const canRequestTenantCreation = PUBLIC_ACCOUNT_SIGNUP_ENABLED;
  const [accountCode, setAccountCode] = useState(() => localStorage.getItem('account_code') || '');
  const [loginMode, setLoginMode] = useState('tenant');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isTenantFormOpen, setIsTenantFormOpen] = useState(initialTenantFormOpen && canRequestTenantCreation);
  const [tenantForm, setTenantForm] = useState(INITIAL_TENANT_FORM);
  const [tenantLoading, setTenantLoading] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const isPlatformMode = loginMode === 'platform';
    try {
      // Utiliser l'email comme identifiant (accepté par notre endpoint custom)
      const response = await authAPI.login(
        isPlatformMode
          ? { email, password, platform_admin: true }
          : { email, password, account_code: accountCode.trim() },
      );
      const {
        access,
        user,
        account_id: accountId = null,
        account_code: resolvedAccountCode = '',
        account_name: accountName = '',
        current_account: currentAccount = null,
      } = response.data;
      
      // Login avec les données utilisateur
      login(
        {
          ...user,
          account_id: accountId,
          account_code: resolvedAccountCode,
          account_name: accountName,
          current_account: currentAccount,
        },
        access,
        resolvedAccountCode,
        {
          account_id: accountId,
          account_code: resolvedAccountCode,
          account_name: accountName,
          current_account: currentAccount,
        },
      );
      localStorage.setItem('token', access);
      localStorage.setItem('access_token', access);
      if (resolvedAccountCode) {
        localStorage.setItem('account_code', resolvedAccountCode);
      } else {
        localStorage.removeItem('account_code');
      }
      
      toast.success('Connexion réussie !');
      navigate(!resolvedAccountCode && (user?.is_staff || user?.is_superuser) ? '/accounts' : '/dashboard');
    } catch (error) {
      const errorPayload = error.response?.data;
      console.error('Login error:', {
        status: error.response?.status,
        code: errorPayload?.code,
        payload: errorPayload,
        message: error.message,
      });
      let errorMessage = getApiErrorMessage(error, "Identifiant ou mot de passe incorrect");
      
      if (error.response?.status === 401 && !error.response?.data?.error) {
        errorMessage = "Identifiant ou mot de passe incorrect";
      } else if (error.response?.status >= 500 && !error.response?.data?.error) {
        errorMessage = "Erreur serveur, veuillez réessayer";
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = "Erreur de connexion, vérifiez votre internet";
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantLocalityCountChange = (event) => {
    const nextCount = Math.min(3, Math.max(1, Number(event.target.value) || 1));
    setTenantForm((current) => {
      const nextLocations = [...current.initial_locations];
      while (nextLocations.length < nextCount) {
        nextLocations.push(createEmptyTenantLocation());
      }
      return {
        ...current,
        locality_count: nextCount,
        initial_locations: nextLocations.slice(0, 3),
      };
    });
  };

  const updateTenantLocation = (index, field, value) => {
    setTenantForm((current) => {
      const nextLocations = [...current.initial_locations];
      nextLocations[index] = {
        ...(nextLocations[index] || createEmptyTenantLocation()),
        [field]: value,
      };
      return {
        ...current,
        initial_locations: nextLocations,
      };
    });
  };

  const handleTenantSubmit = async (e) => {
    e.preventDefault();
    if (!canRequestTenantCreation) {
      toast.error('La création publique de tenant est désactivée.');
      return;
    }
    setTenantLoading(true);

    const initialAdminPassword = tenantForm.initial_admin.password;
    const initialAdminPassword2 = tenantForm.initial_admin.password2;
    if (initialAdminPassword || initialAdminPassword2) {
      if (initialAdminPassword !== initialAdminPassword2) {
        toast.error('Les mots de passe admin ne correspondent pas.');
        setTenantLoading(false);
        return;
      }
    }

    const selectedLocations = tenantForm.initial_locations.slice(0, tenantForm.locality_count);
    const normalizedLocations = selectedLocations.map((location) => ({
      store_name: location.store_name.trim(),
      code: location.code.trim().toLowerCase(),
      city: location.city.trim(),
      address_line: location.address_line.trim(),
      postal_code: location.postal_code.trim(),
      phone: location.phone.trim(),
    }));
    const locationCodes = normalizedLocations.map((location) => location.code).filter(Boolean);
    if (new Set(locationCodes).size !== locationCodes.length) {
      toast.error('Chaque localité doit avoir un code unique.');
      setTenantLoading(false);
      return;
    }

    const payload = {
      account_code: tenantForm.account_code.trim().toLowerCase(),
      name: tenantForm.name.trim(),
      db_url: tenantForm.db_url.trim(),
      initial_admin: {
        first_name: tenantForm.initial_admin.first_name.trim(),
        last_name: tenantForm.initial_admin.last_name.trim(),
        username: tenantForm.initial_admin.username.trim(),
        email: tenantForm.initial_admin.email.trim(),
        password: initialAdminPassword,
      },
      initial_location: normalizedLocations[0],
      initial_locations: normalizedLocations,
    };

    try {
      const response = await settingsAPI.requestTenantCreation(payload);
      const provisioningRun = response.data?.latest_provisioning_run;
      const createdAccountCode = response.data?.account_code || payload.account_code;
      const adminIdentifier = payload.initial_admin.email || payload.initial_admin.username;
      const chosenPassword = payload.initial_admin.password;
      const generatedPassword = response.data?.generated_admin_password || '';

      localStorage.setItem('account_code', createdAccountCode);
      setAccountCode(createdAccountCode);
      setEmail(adminIdentifier);
      setPassword(generatedPassword || chosenPassword);
      setTenantForm(INITIAL_TENANT_FORM);
      setIsTenantFormOpen(false);

      if (provisioningRun?.status === 'failed') {
        toast.error(
          provisioningRun?.error_details?.message
            || 'Le tenant a été enregistré mais le provisioning a échoué.',
        );
      } else if (provisioningRun?.status === 'success') {
        toast.success(chosenPassword ? 'Tenant créé avec le mot de passe choisi.' : 'Tenant créé. Le mot de passe admin a été généré.');
      } else {
        toast.success('Demande de création du tenant enregistrée.');
      }
    } catch (error) {
      console.error('Tenant creation error:', error);
      toast.error(getApiErrorMessage(error, 'Création du tenant impossible.'));
    } finally {
      setTenantLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[minmax(0,1.12fr)_minmax(20rem,32rem)] xl:grid-cols-[minmax(0,1.18fr)_minmax(22rem,34rem)] font-sans bg-[#0d1117]">

      {/* Panneau gauche — identité de marque */}
      <div className="relative hidden overflow-hidden p-8 lg:flex lg:flex-col lg:justify-between xl:p-12">
        {/* Halos */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-0 h-[clamp(16rem,28vw,25rem)] w-[clamp(22rem,42vw,37.5rem)] -translate-x-1/4 translate-y-1/3 rounded-full bg-green-500 opacity-[0.07] blur-[80px]" />
          <div className="absolute top-0 right-0 h-[clamp(12rem,20vw,18.75rem)] w-[clamp(16rem,28vw,25rem)] -translate-y-1/3 translate-x-1/4 rounded-full bg-blue-500 opacity-[0.05] blur-[80px]" />
        </div>

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex h-14 w-[clamp(10rem,24vw,12.5rem)] items-center justify-center rounded-2xl border border-emerald-400/15 bg-[#111827] px-4 shadow-[0_14px_28px_rgba(13,17,23,0.22)]">
            <img
              src={BICICLO_LOGO_SRC}
              alt="Logo Biciclo ERP"
              className="h-full w-auto max-w-none object-contain"
            />
          </div>
        </div>

        {/* Accroche */}
        <div className="relative z-10">
          <h2 className="mb-5 text-4xl font-bold leading-[1.1] tracking-tight text-[#f0f0ec] xl:text-5xl">
            Gérez votre<br />atelier{' '}
            <span className="text-green-400">sans friction.</span>
          </h2>
          <p className="text-[rgba(240,240,236,0.5)] text-[15px] leading-relaxed max-w-xs">
            Réparations, caisse, devis, fournisseurs — tout votre workflow en un seul espace.
          </p>
        </div>

        {/* Stats */}
        <div className="flex gap-10 relative z-10">
          {[
            { num: '1 200+', label: 'Réparations' },
            { num: '98%',    label: 'Satisfaction' },
            { num: '5 min',  label: 'Prise en main' },
          ].map(({ num, label }) => (
            <div key={label} className="flex flex-col gap-0.5">
              <span className="text-[#f0f0ec] text-xl font-semibold tracking-tight">{num}</span>
              <span className="text-[rgba(240,240,236,0.4)] text-[11px] uppercase tracking-widest">{label}</span>
            </div>
          ))}
        </div>

        {/* Vélo décoratif */}
        <svg className="absolute bottom-[-20px] right-[-40px] opacity-[0.04]" width="400" height="300" viewBox="0 0 400 300" fill="none">
          <circle cx="100" cy="220" r="80" stroke="white" strokeWidth="6" />
          <circle cx="300" cy="220" r="80" stroke="white" strokeWidth="6" />
          <path d="M100 220L200 80L280 160M200 80L250 60M200 160H300" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="200" cy="80" r="12" fill="white" />
        </svg>
      </div>

      {/* Panneau droit — formulaire */}
      <div className="flex items-center justify-center bg-[#f7f6f1] px-6 py-12 sm:px-8 sm:py-16 lg:px-12 xl:px-16">
        <div className="w-full max-w-sm">

          {/* Header mobile only */}
          <div className="mb-10 flex lg:hidden">
            <div className="flex h-12 w-[clamp(9.5rem,42vw,11.5rem)] items-center justify-center rounded-2xl border border-[#d7e8db] bg-[#111827] px-4 shadow-sm ring-1 ring-[#dfe7dd]">
              <img
                src={BICICLO_LOGO_SRC}
                alt="Logo Biciclo ERP"
                className="h-full w-auto max-w-none object-contain"
              />
            </div>
          </div>

          <p className="text-green-600 text-xs font-semibold uppercase tracking-widest mb-2">Bienvenue</p>
          <h1 className="text-[#0d1117] text-3xl font-bold tracking-tight mb-2">Connexion</h1>
          <p className="text-[#888880] text-sm mb-10">Accédez à votre espace de gestion.</p>

          <div className="mb-5 grid grid-cols-2 rounded-[12px] border border-[#e0dfd6] bg-white p-1">
            <button
              type="button"
              onClick={() => setLoginMode('tenant')}
              className={`rounded-[9px] px-3 py-2 text-xs font-semibold transition ${
                loginMode === 'tenant'
                  ? 'bg-[#0d1117] text-[#f7f6f1]'
                  : 'text-[#66665f] hover:bg-[#f1f0e9]'
              }`}
            >
              Compte magasin
            </button>
            <button
              type="button"
              onClick={() => setLoginMode('platform')}
              className={`rounded-[9px] px-3 py-2 text-xs font-semibold transition ${
                loginMode === 'platform'
                  ? 'bg-[#0d1117] text-[#f7f6f1]'
                  : 'text-[#66665f] hover:bg-[#f1f0e9]'
              }`}
            >
              Admin plateforme
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {loginMode === 'tenant' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-[#444440] text-[11px] font-semibold uppercase tracking-wide">
                  Code compte *
                </label>
                <input
                  type="text"
                  value={accountCode}
                  onChange={(e) => setAccountCode(e.target.value)}
                  placeholder="ex. magasin_principal"
                  required
                  autoComplete="organization"
                  className="w-full px-3.5 py-3 bg-white border border-[#e0dfd6] rounded-[10px] text-[#0d1117] text-sm placeholder-[#c0bfb6] outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/10 transition-all"
                />
                <p className="text-[11px] leading-relaxed text-[#888880]">
                  Renseignez le code de votre compte pour accéder au bon espace.
                </p>
              </div>
            )}

            {/* Identifiant */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[#444440] text-[11px] font-semibold uppercase tracking-wide">
                Identifiant *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email ou nom d'utilisateur"
                  required
                  className="w-full px-3.5 py-3 pr-10 bg-white border border-[#e0dfd6] rounded-[10px] text-[#0d1117] text-sm placeholder-[#c0bfb6] outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/10 transition-all"
                />
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30 pointer-events-none" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="6" r="3" stroke="#0d1117" strokeWidth="1.5" />
                  <path d="M2 14c0-3.3 2.7-5 6-5s6 1.7 6 5" stroke="#0d1117" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>

            {/* Mot de passe */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[#444440] text-[11px] font-semibold uppercase tracking-wide">
                Mot de passe *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-3.5 py-3 pr-10 bg-white border border-[#e0dfd6] rounded-[10px] text-[#0d1117] text-sm placeholder-[#c0bfb6] outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/10 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 opacity-30 hover:opacity-70 transition-opacity"
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="#0d1117" strokeWidth="1.5" />
                      <line x1="3" y1="3" x2="13" y2="13" stroke="#0d1117" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="#0d1117" strokeWidth="1.5" />
                      <circle cx="8" cy="8" r="2" stroke="#0d1117" strokeWidth="1.5" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-[11px] text-green-600 font-medium hover:opacity-70 transition-opacity">
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-[#0d1117] hover:bg-[#1a2332] text-[#f7f6f1] text-sm font-semibold rounded-[10px] transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 rounded-full border-2 border-[rgba(247,246,241,0.3)] border-t-[#f7f6f1] animate-spin" />
                  Connexion...
                </>
              ) : 'Se connecter'}
            </button>
          </form>

          {canRequestTenantCreation && (
            <div className="mt-6 rounded-[18px] border border-[#d7e8db] bg-white/75 p-4 shadow-sm backdrop-blur-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-green-600">Nouveau tenant</p>
                  <h2 className="mt-1 text-base font-semibold text-[#0d1117]">Créer un nouvel espace</h2>
                  <p className="mt-1 text-[13px] leading-relaxed text-[#66665f]">
                    Ce formulaire prépare le compte, l&apos;admin initial et la première localité du tenant.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsTenantFormOpen((current) => !current)}
                  className="shrink-0 rounded-full border border-[#cfe1d0] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#0d1117] transition hover:bg-[#eef5ee]"
                >
                  {isTenantFormOpen ? 'Masquer' : 'Créer'}
                </button>
              </div>

              {isTenantFormOpen && (
                <form onSubmit={handleTenantSubmit} className="mt-5 space-y-4">
                <div className="rounded-2xl border border-[#e6e5dc] bg-[#fbfaf6] p-4">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#444440]">Compte</h3>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                      Code compte *
                      <input
                        type="text"
                        required
                        value={tenantForm.account_code}
                        onChange={(e) => setTenantForm((current) => ({
                          ...current,
                          account_code: e.target.value,
                        }))}
                        placeholder="magasin_principal"
                        className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all placeholder-[#c0bfb6] focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                      Nom du compte *
                      <input
                        type="text"
                        required
                        value={tenantForm.name}
                        onChange={(e) => setTenantForm((current) => ({
                          ...current,
                          name: e.target.value,
                        }))}
                        placeholder="Magasin principal"
                        className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all placeholder-[#c0bfb6] focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440] sm:col-span-2">
                      URL base technique
                      <input
                        type="text"
                        value={tenantForm.db_url}
                        onChange={(e) => setTenantForm((current) => ({
                          ...current,
                          db_url: e.target.value,
                        }))}
                        placeholder="postgresql://tenant:secret@db.example.com:5432/magasin_principal"
                        className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all placeholder-[#c0bfb6] focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      />
                    </label>
                  </div>
                </div>

                <div className="rounded-2xl border border-[#e6e5dc] bg-[#fbfaf6] p-4">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#444440]">Admin initial</h3>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                      Prénom *
                      <input
                        type="text"
                        required
                        value={tenantForm.initial_admin.first_name}
                        onChange={(e) => setTenantForm((current) => ({
                          ...current,
                          initial_admin: { ...current.initial_admin, first_name: e.target.value },
                        }))}
                        className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                      Nom *
                      <input
                        type="text"
                        required
                        value={tenantForm.initial_admin.last_name}
                        onChange={(e) => setTenantForm((current) => ({
                          ...current,
                          initial_admin: { ...current.initial_admin, last_name: e.target.value },
                        }))}
                        className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                      Nom d&apos;utilisateur *
                      <input
                        type="text"
                        required
                        value={tenantForm.initial_admin.username}
                        onChange={(e) => setTenantForm((current) => ({
                          ...current,
                          initial_admin: { ...current.initial_admin, username: e.target.value },
                        }))}
                        className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                      Email *
                      <input
                        type="email"
                        required
                        value={tenantForm.initial_admin.email}
                        onChange={(e) => setTenantForm((current) => ({
                          ...current,
                          initial_admin: { ...current.initial_admin, email: e.target.value },
                        }))}
                        className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440] sm:col-span-2">
                      Mot de passe initial
                      <input
                        type="password"
                        minLength={8}
                        value={tenantForm.initial_admin.password}
                        onChange={(e) => setTenantForm((current) => ({
                          ...current,
                          initial_admin: { ...current.initial_admin, password: e.target.value },
                        }))}
                        placeholder="Laisser vide pour générer automatiquement"
                        className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all placeholder-[#c0bfb6] focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440] sm:col-span-2">
                      Confirmer le mot de passe
                      <input
                        type="password"
                        minLength={8}
                        value={tenantForm.initial_admin.password2}
                        onChange={(e) => setTenantForm((current) => ({
                          ...current,
                          initial_admin: { ...current.initial_admin, password2: e.target.value },
                        }))}
                        placeholder="Requis si vous choisissez un mot de passe"
                        className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all placeholder-[#c0bfb6] focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      />
                    </label>
                  </div>
                </div>

                <div className="rounded-2xl border border-[#e6e5dc] bg-[#fbfaf6] p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#444440]">Localités</h3>
                      <p className="mt-1 text-[12px] leading-relaxed text-[#66665f]">
                        Chaque localité sera créée dans la table dédiée du compte.
                      </p>
                    </div>
                    <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                      Nombre
                      <select
                        value={tenantForm.locality_count}
                        onChange={handleTenantLocalityCountChange}
                        className="min-w-24 rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                      >
                        {[1, 2, 3].map((count) => (
                          <option key={count} value={count}>{count}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="mt-4 space-y-4">
                    {tenantForm.initial_locations.slice(0, tenantForm.locality_count).map((location, index) => (
                      <div key={index} className="rounded-2xl border border-[#e0dfd6] bg-white p-4">
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-green-700">
                          Localité {index + 1}
                        </h4>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                            Nom du magasin
                            <input
                              type="text"
                              value={location.store_name}
                              onChange={(e) => updateTenantLocation(index, 'store_name', e.target.value)}
                              placeholder={index === 0 ? 'Magasin principal' : 'Optionnel'}
                              className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all placeholder-[#c0bfb6] focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                            />
                          </label>
                          <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                            Code localité *
                            <input
                              type="text"
                              required
                              value={location.code}
                              onChange={(e) => updateTenantLocation(index, 'code', e.target.value)}
                              placeholder={index === 0 ? 'localite_principale' : 'annexe'}
                              className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all placeholder-[#c0bfb6] focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                            />
                          </label>
                          <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                            Ville *
                            <input
                              type="text"
                              required
                              value={location.city}
                              onChange={(e) => updateTenantLocation(index, 'city', e.target.value)}
                              className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                            />
                          </label>
                          <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                            Téléphone
                            <input
                              type="text"
                              value={location.phone}
                              onChange={(e) => updateTenantLocation(index, 'phone', e.target.value)}
                              className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                            />
                          </label>
                          <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440] sm:col-span-2">
                            Adresse
                            <input
                              type="text"
                              value={location.address_line}
                              onChange={(e) => updateTenantLocation(index, 'address_line', e.target.value)}
                              className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                            />
                          </label>
                          <label className="flex flex-col gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#444440]">
                            Code postal
                            <input
                              type="text"
                              value={location.postal_code}
                              onChange={(e) => updateTenantLocation(index, 'postal_code', e.target.value)}
                              className="rounded-[10px] border border-[#e0dfd6] bg-white px-3.5 py-3 text-sm font-normal normal-case tracking-normal text-[#0d1117] outline-none transition-all focus:border-green-500 focus:ring-2 focus:ring-green-500/10"
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={tenantLoading}
                  className="w-full rounded-[10px] border border-[#0d1117] bg-[#0d1117] px-4 py-3 text-sm font-semibold text-[#f7f6f1] transition-all hover:bg-[#1a2332] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {tenantLoading ? 'Création du tenant...' : 'Créer le tenant'}
                </button>
                </form>
              )}
            </div>
          )}

          {canRequestTenantCreation && (
            <p className="text-center text-[#888880] text-[13px] mt-6">
              Pour un utilisateur existant,{' '}
              <Link to="/register" className="text-[#0d1117] font-semibold border-b border-green-500 pb-px hover:opacity-70 transition-opacity">
                utilisez l&apos;inscription
              </Link>
              .
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
